import { useMemo } from 'react';
import type { Atom, Ctx } from '@reatom/framework';
import { atom, reatomAsync, withAbort, withErrorAtom } from '@reatom/framework';
import { useCtx } from '@reatom/npm-react';

import { createObservableFromAtom } from '@/js/libs/observable/index.ts';
import { getComponentDebugName } from '@/js/libs/reatom-devtools/index.ts';

import { parseError } from './parseError.ts';
import type {
  ApiMethod,
  ApiParameters,
  UseLazyFetchConfig,
  OnFail,
  LazyFetchReturn,
  LazyFetchOptions,
  OnLazySuccess,
  OnUploadProgress,
} from './types.tsx';
import { DEFAULT_QUERY_PARAMS } from './useRequest.tsx';
import { useRequestContextValue } from './UseRequestContext.ts';

type Config<Api extends ApiMethod> = {
  cancelPreviousRequests: Atom<boolean>;
  onFail: Atom<OnFail | undefined>;
  onSuccess: Atom<OnLazySuccess<Api> | undefined>;
  onUploadProgress: Atom<OnUploadProgress | undefined>;
  ignoreErrors: Atom<null | number[]>;
  queryDefaults: Atom<Partial<ApiParameters<Api>> | false>;
  name?: string;
};
export function createLazyFetch<API extends ApiMethod>(apiMethod: API, config: Config<API>, _ctx: Ctx): LazyFetchReturn<API>;
export function createLazyFetch(apiMethod: ApiMethod, config: Config<ApiMethod>, _ctx: Ctx): LazyFetchReturn<ApiMethod> {
  const { ignoreErrors, queryDefaults, name = `${apiMethod.name}Lazy` } = config;
  const cancelPreviousRequests = _ctx.get(config.cancelPreviousRequests);
  const fetchAction = reatomAsync(async (ctx, params, silent = false) => {
    try {
      const response = await ctx.schedule(() =>
        apiMethod({
          params,
          signal: ctx.controller.signal,
          ignoreErrors: ctx.get(ignoreErrors),
          queryDefaults: ctx.get(queryDefaults),
          onUploadProgress: ctx.get(config.onUploadProgress),
        }),
      );
      ctx.get(config.onSuccess)?.(response.data, params, response.status);
      return response;
    } catch (e) {
      // `silent` is captured per call, so concurrent requests don't clobber each other's intent
      const error = parseError(e, name);

      // safety call `onFail` before render
      if (error && !silent) ctx.schedule(() => ctx.get(config.onFail)?.(error));

      throw e;
    }
  }, name).pipe(
    withAbort({ strategy: cancelPreviousRequests ? 'last-in-win' : 'none' }),
    withErrorAtom((_ctx, e) => parseError(e, name)),
  );
  fetchAction.pendingAtom.__reatom.name = fetchAction.pendingAtom.__reatom.name?.replace('pendingAtom', '_pendingAtom');

  const isLoadingAtom = atom((ctx) => ctx.spy(fetchAction.pendingAtom) > 0, `${name}.isLoadingAtom`);

  return {
    error$: createObservableFromAtom(fetchAction.errorAtom),
    pending$: createObservableFromAtom(isLoadingAtom),
    load: (params: Parameters<typeof fetchAction>, options?: LazyFetchOptions) =>
      fetchAction(_ctx, params, options?.silent ?? false)
        .then((response) => response.data)
        .catch((err: unknown) => {
          if (cancelPreviousRequests) {
            // message described inside withAbort
            if (err && typeof err === 'object' && 'message' in err && typeof err.message === 'string' && err.message.startsWith('abort ')) {
              // convert back to default abort error
              const abortError = new Error();
              abortError.name = 'AbortError';
              throw abortError;
            }
          }
          // Rethrow other errors
          throw err;
        }),
    abort: () => fetchAction.abort(_ctx),
  };
}

export function useLazyRequest<API extends ApiMethod>(config: UseLazyFetchConfig<API>): LazyFetchReturn<API>;
export function useLazyRequest(props: UseLazyFetchConfig<ApiMethod>): LazyFetchReturn<ApiMethod> {
  const { onFail: onFailFromContext } = useRequestContextValue();
  const {
    apiMethod,
    cancelPreviousRequests = true,
    onFail: onFailFromProps,
    onSuccess,
    onUploadProgress,
    ignoreErrors,
    queryDefaults = DEFAULT_QUERY_PARAMS,
  } = props;
  const onFail = onFailFromProps ?? onFailFromContext;
  const ctx = useCtx();
  const name = getComponentDebugName(`${apiMethod.name}Lazy`);
  const config = useMemo(
    () => ({
      cancelPreviousRequests: atom(cancelPreviousRequests, `${name}._cancelPreviousRequests`),
      onFail: atom<OnFail | undefined>(undefined, `${name}._onFail`),
      onSuccess: atom<OnLazySuccess<ApiMethod> | undefined>(undefined, `${name}._onSuccess`),
      onUploadProgress: atom<OnUploadProgress | undefined>(undefined, `${name}._onUploadProgress`),
      ignoreErrors: atom(ignoreErrors ?? null),
      queryDefaults: atom(queryDefaults),
      name,
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );
  if (ctx.get(config.onFail) !== onFail) {
    config.onFail(ctx, () => onFail);
  }
  if (ctx.get(config.onSuccess) !== onSuccess) {
    config.onSuccess(ctx, () => onSuccess);
  }
  if (ctx.get(config.onUploadProgress) !== onUploadProgress) {
    config.onUploadProgress(ctx, () => onUploadProgress);
  }
  if (ctx.get(config.cancelPreviousRequests) !== cancelPreviousRequests) {
    config.cancelPreviousRequests(ctx, () => cancelPreviousRequests);
  }

  return useMemo(() => createLazyFetch(apiMethod, config, ctx), [apiMethod, config, ctx]);
}
